import { call } from "redux-saga/effects";
import action from "../../decorators/action.decorator";
import reducer, { InitSchemaReducer } from "../../decorators/reducer.decorator";
import { DIContainerKeys } from "../../di-container/di-container.keys";
import { Entities } from "../../store/entities/entities.enum";
import { HttpService } from "../http.service";
import { ReducerMethods } from "../../store/reducer.methods";
import { BASE_API_PAGE_ENUMS, BASE_API_PATHS, API_DOMENS } from "../../../constants/apis.constants.js";
import loader from "../../decorators/loader.decorator";
import { Loaders } from "../../store/loaders/loaders.enum";
import { CallToast } from "../../../Components/PriceCalculator/modals/CustomToast";
import flagger from "../../decorators/flagger.decorator";
import { Flaggers } from "../../store/flaggers/flaggers.enum";
import { GetAddToolsForStackResponse } from "./dto/get-add-tools-for-stack.dto";
import { GetToolsInMyStackResponse } from "./dto/get-tools-in-my-stack.dto";
import type { SaveToolsToMyStackDto, SaveToolsToMyStackResponse } from "./dto/save-tools-to-my-stack.dto";
import type { RemoveToolFromStackDto, RemoveToolFromStackResponse } from "./dto/remove-tool-from-stack.dto";
import type { UpdateToolDto, UpdateToolResponse } from "./dto/get-tool-info.dto";

export const ToolEffectActions = {
  updateTool: `${Entities.Tool}_updateTool`,
  getAddToolsForStack: `${Entities.Tool}_getAddToolsForStack`,
  getToolsInMyStack: `${Entities.Tool}_getToolsInMyStack`,
  saveToolsToMyStack: `${Entities.Tool}_saveToolsToMyStack`,
  removeToolFromStack: `${Entities.Tool}_removeToolFromStack`,
};

const initSchema: InitSchemaReducer = {
  definitions: {},
  options: { idAttribute: '' },
};

@reducer({
  entityName: Entities.Tool,
  initSchema,
  aliasName: DIContainerKeys.ToolService,
})
@reducer({
  entityName: Entities.ToolInStack,
  initSchema,
  aliasName: DIContainerKeys.ToolService,
})
export class ToolService extends HttpService {
  @loader(Loaders.GetAddToolsForStackLoader)
  @action(DIContainerKeys.ToolService)
  public *getAddToolsForStack(): Generator<any, void, any> {
    yield this.setLoaderState(Loaders.GetAddToolsForStackLoader, true);
    const response: GetAddToolsForStackResponse = yield call(
      this.get,
      {
        url: this.pageEnum(BASE_API_PAGE_ENUMS.GetAddToolsForStack),
        domain: API_DOMENS.CC_API,
      },
      ReducerMethods.UPDATE
    );
    if (!response?.mr?.Success) {
      CallToast("Something went wrong", false, false, "v2style");
      yield this.setLoaderState(Loaders.GetAddToolsForStackLoader, false);
      return;
    }
    const { mr, ...payload } = response;
    yield this.setEntityState(Entities.Tool, payload);
    yield this.setLoaderState(Loaders.GetAddToolsForStackLoader, false);
  }

  @loader(Loaders.GetToolsInMyStackLoader)
  @action(DIContainerKeys.ToolService)
  public *getToolsInMyStack(): Generator<any, void, any> {
    yield this.setLoaderState(Loaders.GetToolsInMyStackLoader, true);
    const response: GetToolsInMyStackResponse = yield call(
      this.get,
      {
        url: this.pageEnum(BASE_API_PAGE_ENUMS.GetToolsInMyStack),
        domain: API_DOMENS.CC_API,
      },
      ReducerMethods.UPDATE
    );
    if (!response?.mr?.Success) {
      CallToast("Something went wrong", false, false, "v2style");
      yield this.setLoaderState(Loaders.GetToolsInMyStackLoader, false);
      return;
    }
    const { mr, ...payload } = response;
    yield this.setEntityState(Entities.ToolInStack, payload);
    yield this.setLoaderState(Loaders.GetToolsInMyStackLoader, false);
  }

  @loader(Loaders.UpdateToolLoader)
  @flagger(Flaggers.UpdateToolModalFlagger)
  @flagger(Flaggers.UpdatePracticeModalFlagger)
  @flagger(Flaggers.AddToolsModalFlagger)
  @action(DIContainerKeys.ToolService)
  public *updateTool(dto: UpdateToolDto): Generator<any, void, any> {
    yield this.setLoaderState(Loaders.UpdateToolLoader, true);

    const response: UpdateToolResponse = yield call(
      this.post,
      {
        url: BASE_API_PATHS.createMultipleIntegrationTechnologyToolV2,
        body: this.toFormData({
          ObjectId: dto.ToolId,
          ToolName: dto.ToolName,
          Selected: true,
          IsGovernanceAndResilence: false,
          optionEnum99: 26,
          IsPractice: dto.IsPractice,
          PracticeToolId_List: JSON.stringify(dto.PracticeToolIdsList ?? []),
          IsMultipleIntegrationAllowed: true,
          IntegrationTechnology_List: JSON.stringify(dto.technologyIdsList ?? []),
          IntegrationInfo_List: JSON.stringify(dto.IntegrationInfo_List ?? []),
          ObjId_Customer: dto.clientId,
          IntegrationTechnologyType_List: JSON.stringify(dto.technologiesList ?? []),
          TechnologyPracticeDetail_List: dto.PracticeType_List ? JSON.stringify(dto.PracticeType_List) : undefined,
          ThisToolOnly: !!dto.isToolUpdate,
        }),
        domain: API_DOMENS.CC_API,
      },
      ReducerMethods.UPDATE
    );

    if (!response?.mr?.Success) {
      CallToast("Something went wrong", false, false, "v2style");
      yield this.setLoaderState(Loaders.UpdateToolLoader, false);
      return;
    }

    yield this.setLoaderState(Loaders.UpdateToolLoader, false);
    yield this.setFlaggerState(Flaggers.UpdateToolModalFlagger, null);
    yield this.setFlaggerState(Flaggers.UpdatePracticeModalFlagger, null);
    
    yield this.getToolsInMyStack();
    if (dto.callback) {
      dto.callback(response);
    }
  }

  @loader(Loaders.SaveToolsToMyStackLoader)
  @action(DIContainerKeys.ToolService)
  public *saveToolsToMyStack(dto: SaveToolsToMyStackDto): Generator<any, void, any> {
    yield this.setLoaderState(Loaders.SaveToolsToMyStackLoader, true);
    
    const response: SaveToolsToMyStackResponse = yield call(
      this.post,
      {
        url: BASE_API_PATHS.addIntegrationToMultipleTechnologiesForMSP,
        domain: API_DOMENS.CC_API,
        body: this.toFormData({
          IntegrationTechnology_List: JSON.stringify(dto.toolIds ?? []),
        }),
      },
      ReducerMethods.UPDATE
    );
    
    if (!response?.mr?.Success) {
      CallToast("Something went wrong", false, false, "v2style");
      yield this.setLoaderState(Loaders.SaveToolsToMyStackLoader, false);
      return;
    }
    
    CallToast("Tools saved successfully", true, false, "v2style");
    yield this.setLoaderState(Loaders.SaveToolsToMyStackLoader, false);
    yield this.setFlaggerState(Flaggers.AddToolsModalFlagger, null);
    
    yield this.getToolsInMyStack();
  }

  @loader(Loaders.RemoveToolFromStackLoader)
  @action(DIContainerKeys.ToolService)
  public *removeToolFromStack(dto: RemoveToolFromStackDto): Generator<any, void, any> {
    yield this.setLoaderState(Loaders.RemoveToolFromStackLoader, true);
    
    const pageEnum = dto.isCustom ? BASE_API_PAGE_ENUMS.DeleteIntegration : BASE_API_PAGE_ENUMS.RemoveToolFromStack;
    const response: RemoveToolFromStackResponse = yield call(
      this.get,
      {
        url: this.pageEnum(pageEnum),
        params: {
          optionEnum1: dto.toolId,
          optionEnum2: 0,
        },
        domain: API_DOMENS.CC_API,
      },
      ReducerMethods.UPDATE
    );
    
    if (!response?.Success) {
      CallToast("Something went wrong", false, false, "v2style");
      yield this.setLoaderState(Loaders.RemoveToolFromStackLoader, false);
      return;
    }
    
    yield this.setLoaderState(Loaders.RemoveToolFromStackLoader, false);
    yield this.setFlaggerState(Flaggers.UpdateToolModalFlagger, null);
    yield this.getToolsInMyStack();
    if (dto.callback) {
      dto.callback();
    }
  }
}
